import { Holding } from "../../../../../services/holdings";
import {
  byDecimalString,
  byNumber,
  byText,
  SortRule,
  sortRows,
} from "../../../../../utils";
import { ALL_ASSETS_LABEL, ASSET_GROUP_LABELS } from "./constants";
import {
  HoldingAssetFilter,
  HoldingAssetGroup,
  HoldingCounts,
  HoldingFacetOption,
  HoldingFilters,
  HoldingSortKey,
  HoldingSortState,
  HoldingStatusFilter,
} from "./types";

// CRYPTO and FOREX fold in with OTHER: the section facets on the three groups the
// mockup displays, not on the five values the instrument catalogue stores.
export const assetGroupOf = (holding: Holding): HoldingAssetGroup => {
  const { assetType } = holding.instrument;
  return assetType === "STOCK" || assetType === "ETF" ? assetType : "OTHER";
};

const ASSET_GROUP_ORDER: HoldingAssetGroup[] = ["STOCK", "ETF", "OTHER"];

export const assetFacetOptions = (
  rows: Holding[],
  selected: HoldingAssetFilter,
): HoldingFacetOption[] => {
  const options: HoldingFacetOption[] = [
    { value: "ALL", label: `${ALL_ASSETS_LABEL} · ${rows.length}` },
  ];

  for (const group of ASSET_GROUP_ORDER) {
    const count = rows.filter((row) => assetGroupOf(row) === group).length;
    // The selected group is kept at `· 0` rather than dropped: without it the control
    // falls back to "All instruments" while the filter it names is still narrowing the list.
    if (count > 0 || group === selected) {
      options.push({
        value: group,
        label: `${ASSET_GROUP_LABELS[group]} · ${count}`,
      });
    }
  }

  return options;
};

/**
 * Prior tickers are searchable on purpose: a position renamed OLD→NEW is otherwise
 * unfindable by the symbol its own trades were recorded under.
 */
export const matchesHoldingSearch = (
  holding: Holding,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return true;
  }

  const haystack = [
    holding.instrument.symbol,
    holding.instrument.name,
    ...holding.priorListings.map((listing) => listing.instrument.symbol),
  ];

  return haystack.some((value) => value.toLowerCase().includes(needle));
};

export const hasStatus = (
  holding: Holding,
  status: HoldingStatusFilter,
): boolean => (status === "closed" ? holding.isClosed : !holding.isClosed);

/** Narrows a status partition — the partition itself is taken with `hasStatus` first. */
export const filterHoldings = (
  rows: Holding[],
  { assetGroup, query }: HoldingFilters,
): Holding[] =>
  rows
    .filter((row) => assetGroup === "ALL" || assetGroupOf(row) === assetGroup)
    .filter((row) => matchesHoldingSearch(row, query));

export const HOLDING_SORT_RULES: Record<HoldingSortKey, SortRule<Holding>> = {
  instrument: byText(
    (row) => `${row.instrument.symbol} ${row.instrument.name}`,
  ),
  quantity: byDecimalString((row) => row.quantity),
  marketPrice: byNumber((row) => row.latestPriceEur),
  costBasis: byNumber((row) => row.remainingCostBasis),
  positionValue: byNumber((row) => row.positionValue),
  unrealizedReturnValue: byNumber((row) => row.unrealizedReturnValue),
};

export const sortHoldings = (
  rows: Holding[],
  sort: HoldingSortState,
): Holding[] => sortRows(rows, HOLDING_SORT_RULES, sort);

export const holdingCounts = (
  all: Holding[],
  openRows: Holding[],
  statusRows: Holding[],
  filteredRows: Holding[],
): HoldingCounts => ({
  open: openRows.length,
  closed: all.length - openRows.length,
  filtered: filteredRows.length,
  total: statusRows.length,
});

export const pageCountOf = (total: number, size: number): number =>
  Math.max(1, Math.ceil(total / size));

export const clampPage = (page: number, pageCount: number): number =>
  Math.min(Math.max(page, 0), pageCount - 1);

export const pageSlice = <Row>(
  rows: Row[],
  page: number,
  size: number,
): Row[] => rows.slice(page * size, page * size + size);
